import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import layoutStyles from './AdminCurriculumVideo.module.css';
import { SuccessToast, useSuccessToast } from './shared/SuccessToast';
import CfpSessionDocumentsDialog from './dashboard/schedule-cfp-session/CfpSessionDocumentsDialog';
import {
  CFP_SESSION_STATUS,
  matchesAdminCfpSessionStatusFilter,
} from './dashboard/schedule-cfp-session/cfpSessionStatus';
import {
  fetchAdminUserCfpSessions,
  fetchExperts,
  getToken,
  handleApiError,
  markAdminCfpSessionDone,
} from '../utils/api';
import { useContentCatalog } from '../utils/contentCatalog';
import SessionsTable from './AdminUserCfpSessions/SessionsTable';
import { MarkSessionDoneModal } from './AdminUserCfpSessions/modals';
import {
  buildExpertNameMap,
  CfpSessionsHeader,
  CfpSessionsLoadingState,
  getAdminSessionInfo,
  mapSessionRow,
} from './AdminUserCfpSessions/helpers';

const AdminUserCfpSessions = () => {
  const navigate = useNavigate();
  const { catalog } = useContentCatalog();
  const statusCopy = catalog?.cfp_session_statuses;

  const [adminInfo, setAdminInfo] = useState({ email: '', fullName: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessions, setSessions] = useState([]);
  const [experts, setExperts] = useState([]);
  const [statusFilter, setStatusFilter] = useState('paid');
  const [documentsSession, setDocumentsSession] = useState(null);
  const [doneConfirmSession, setDoneConfirmSession] = useState(null);
  const [doneSubmitting, setDoneSubmitting] = useState(false);
  const [doneConfirmError, setDoneConfirmError] = useState('');
  const { message: successMessage, isExiting: successMessageExiting, showToast: showSuccessMessage } = useSuccessToast();

  useEffect(() => {
    const token = getToken();
    if (!token) {
      handleApiError(new Error('No token found'), navigate, true);
      return undefined;
    }

    setAdminInfo(getAdminSessionInfo());

    const abort = new AbortController();
    (async () => {
      setLoading(true);
      setError('');

      const [result, expertsData] = await Promise.all([
        fetchAdminUserCfpSessions(navigate, abort.signal),
        fetchExperts(abort.signal),
      ]);
      if (abort.signal.aborted) return;

      if (expertsData) setExperts(expertsData);

      if (result.ok) {
        setSessions(Array.isArray(result.sessions) ? result.sessions : []);
      } else {
        setError(result.message || 'Failed to fetch user CFP sessions');
      }
      setLoading(false);
    })();

    return () => abort.abort();
  }, [navigate]);

  const expertNameById = useMemo(() => buildExpertNameMap(experts), [experts]);

  const sessionRows = useMemo(
    () => sessions
      .filter((row) => matchesAdminCfpSessionStatusFilter(row, statusFilter))
      .map((row, index) => mapSessionRow(row, expertNameById, index, statusCopy)),
    [sessions, statusFilter, expertNameById, statusCopy],
  );

  const openDocumentsDialog = useCallback((row, topicName) => {
    setDocumentsSession({
      id: row?.id,
      topic: topicName,
    });
  }, []);

  const closeDocumentsDialog = useCallback(() => {
    setDocumentsSession(null);
  }, []);

  const openDoneConfirm = useCallback((row) => {
    setDoneConfirmError('');
    setDoneConfirmSession(row);
  }, []);

  const closeDoneConfirm = useCallback(() => {
    if (doneSubmitting) return;
    setDoneConfirmSession(null);
    setDoneConfirmError('');
  }, [doneSubmitting]);

  const confirmMarkSessionDone = useCallback(async () => {
    const sessionId = String(doneConfirmSession?.id ?? '').trim();
    if (!sessionId) return;

    setDoneSubmitting(true);
    setDoneConfirmError('');

    try {
      const result = await markAdminCfpSessionDone({ id: sessionId }, navigate);
      if (!result.ok) {
        setDoneConfirmError(result.message || 'Failed to mark session as done.');
        return;
      }

      setSessions((prev) =>
        prev.map((row) =>
          String(row?.id) === sessionId
            ? { ...row, status: CFP_SESSION_STATUS.DONE }
            : row,
        ),
      );
      showSuccessMessage(result.message || 'CFP session marked as done successfully.');
      setDoneConfirmSession(null);
      setDoneConfirmError('');
    } finally {
      setDoneSubmitting(false);
    }
  }, [doneConfirmSession, navigate, showSuccessMessage]);

  const handleLogout = useCallback(() => {
    localStorage.clear();
    navigate('/admin-login');
  }, [navigate]);

  const handleBack = useCallback(() => {
    navigate('/admin-dashboard');
  }, [navigate]);

  return (
    <div className={layoutStyles['admin-curriculum-container']}>
      <CfpSessionsHeader
        adminInfo={adminInfo}
        onBack={handleBack}
        onLogout={handleLogout}
      />

      <main className={layoutStyles['admin-main-content']} id="admin-user-cfp-sessions-main">
        <SuccessToast message={successMessage} isExiting={successMessageExiting} />
        {error && (
          <div className={layoutStyles['error-message']} role="alert">
            {error}
          </div>
        )}

        {loading ? (
          <CfpSessionsLoadingState />
        ) : (
          <SessionsTable
            rows={sessionRows}
            statusFilters={statusCopy?.admin_filters || []}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            showActionColumn={statusFilter === 'paid'}
            onOpenDocuments={openDocumentsDialog}
            onOpenDoneConfirm={openDoneConfirm}
          />
        )}
      </main>

      <CfpSessionDocumentsDialog
        session={documentsSession}
        navigate={navigate}
        readOnly
        onClose={closeDocumentsDialog}
      />

      <MarkSessionDoneModal
        session={doneConfirmSession}
        submitting={doneSubmitting}
        error={doneConfirmError}
        onClose={closeDoneConfirm}
        onConfirm={confirmMarkSessionDone}
      />
    </div>
  );
};

export default AdminUserCfpSessions;
